import { useEffect, useState } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const TTL_MS = 5 * 60 * 1000;

let cache = null;
let cachedAt = 0;
let failedAt = 0;
let inflight = null;

function fresh() {
  return cache && Date.now() - cachedAt < TTL_MS ? cache : null;
}

// A failed load (403 for a user without exec read, network error) is remembered for a minute so every tooltip
// on the page does not re-request it.
export function fetchMetricDefenses() {
  const hit = fresh();
  if (hit) return Promise.resolve(hit);
  if (Date.now() - failedAt < 60 * 1000) return Promise.reject(new Error('defenses unavailable'));
  if (!inflight) {
    inflight = fetch(`${API_BASE_URL}/methodology/defenses`, { credentials: 'include' })
      .then((r) => {
        if (!r.ok) throw new Error(`defenses ${r.status}`);
        return r.json();
      })
      .then((d) => {
        const byKey = {};
        (d.metrics || []).forEach((m) => { byKey[m.key] = m; });
        cache = { ...d, byKey };
        cachedAt = Date.now();
        return cache;
      })
      .catch((e) => { failedAt = Date.now(); throw e; })
      .finally(() => { inflight = null; });
  }
  return inflight;
}

export function resetMetricDefensesCache() {
  cache = null;
  cachedAt = 0;
  failedAt = 0;
  inflight = null;
}

// Returns the register payload ({ metrics, byKey, themes, ... }) or null while loading / unavailable.
// `enabled=false` makes no request (tooltips without a metricKey).
export function useMetricDefenses(enabled = true) {
  const [data, setData] = useState(enabled ? fresh() : null);
  useEffect(() => {
    let live = true;
    if (enabled) {
      fetchMetricDefenses().then((d) => { if (live) setData(d); }).catch(() => {});
    }
    return () => { live = false; };
  }, [enabled]);
  return enabled ? data : null;
}

export const STATUS_LABELS = {
  defensible: 'Defensible',
  defensible_with_limits: 'Defensible, with limits',
  known_limitation_fix_planned: 'Known limitation, fix planned',
  withdrawn: 'Withdrawn',
};

export const STATUS_COLORS = {
  defensible: 'success',
  defensible_with_limits: 'info',
  known_limitation_fix_planned: 'warning',
  withdrawn: 'error',
};

export function methodologyHref(metricKey) {
  return `/methodology?metric=${encodeURIComponent(metricKey)}`;
}
